import { useCallback, useEffect, useRef, useState } from 'react'
import { Nullable } from '../../../common/Nullable'
import { useLogger } from '../../../components/Logger/hooks/useLogger'
import * as Sentry from '@sentry/react'

export const useAudioContext = () => {
  const [audioContext, setAudioContext] = useState<Nullable<AudioContext>>(() => new AudioContext())
  const isClosingRef = useRef(false)
  const logger = useLogger()

  const closeAudioContext = useCallback(async () => {
    const ctx = audioContext
    if (!ctx || ctx.state === 'closed' || isClosingRef.current) return

    isClosingRef.current = true
    try {
      await ctx.close()
    } catch (error: unknown) {
      Sentry.captureException(error)
      await logger.warn('[useAudioContext] Error closing AudioContext:', error)
    } finally {
      isClosingRef.current = false
      setAudioContext(null)
    }
  }, [audioContext, logger])

  const resumeAudioContext = useCallback(async () => {
    if (!audioContext) {
      const newCtx = new AudioContext()
      setAudioContext(newCtx)
      return newCtx
    }

    if (audioContext.state === 'suspended') {
      try {
        await logger.info('[useAudioContext] Resuming AudioContext...')
        await audioContext.resume()
      } catch (error: unknown) {
        Sentry.captureException(error)
        await logger.warn('[useAudioContext] Error resuming AudioContext:', error)
      }
    }

    return audioContext
  }, [audioContext, logger])

  const suspendAudioContext = useCallback(async () => {
    if (audioContext && audioContext.state === 'running') {
      try {
        await logger.info('[useAudioContext] Suspending AudioContext...')
        await audioContext.suspend()
      } catch (error: unknown) {
        Sentry.captureException(error)
        await logger.warn('[useAudioContext] Error suspending AudioContext:', error)
      }
    }
  }, [audioContext, logger])

  useEffect(() => {
    return () => {
      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close().catch((error: unknown) => {
          Sentry.captureException(error)
          logger.error('[useAudioContext] Error closing AudioContext on unmount:', error)
        })
      }
    }
  }, [audioContext, logger])

  return { audioContext, closeAudioContext, suspendAudioContext, resumeAudioContext }
}
